import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Play, ChevronRight, Star } from 'lucide-react';
import api from '../services/api';

const RecentShows = ({ limit = 4, showHeader = true, className = "" }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRecentShows();
    }, []);

    const fetchRecentShows = async () => {
        try {
            setLoading(true);
            // Get past events (public endpoint)
            const response = await api.get(`/events/past?limit=${limit}&order_by=start_date&order_dir=DESC`);
            if (response.data.success) {
                setEvents(response.data.data || []);
            } else {
                setError('Failed to load recent shows');
            }
        } catch (err) {
            console.error('Error fetching recent shows:', err);
            setError('Failed to load recent shows');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getEventTypeColor = (type) => {
        const colors = {
            'concert': 'text-purple-400 bg-purple-500/20',
            'festival': 'text-pink-400 bg-pink-500/20',
            'livestream': 'text-blue-400 bg-blue-500/20',
            'workshop': 'text-green-400 bg-green-500/20',
            'meetup': 'text-yellow-400 bg-yellow-500/20',
            'release': 'text-red-400 bg-red-500/20'
        };
        return colors[type] || 'text-gray-400 bg-gray-500/20';
    };


    const calculateDaysAgo = (dateString) => {
        const eventDate = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - eventDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return `${Math.floor(diffDays / 30)} months ago`;
    };

    if (loading) {
        return (
            <div className={`recent-shows-widget ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Calendar className="w-6 h-6 text-green-400" />
                        <h2 className="text-xl font-bold text-white">Past Events</h2>
                    </div>
                )}
                
                <div className="space-y-4">
                    {[...Array(limit)].map((_, index) => (
                        <div key={index} className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50 animate-pulse">
                            <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-700 rounded w-1/2 mb-2"></div>
                            <div className="h-3 bg-gray-700 rounded w-1/4"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`recent-shows-widget ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Calendar className="w-6 h-6 text-green-400" />
                        <h2 className="text-xl font-bold text-white">Past Events</h2>
                    </div>
                )}
                
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎪</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className={`recent-shows-widget ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Calendar className="w-6 h-6 text-green-400" />
                        <h2 className="text-xl font-bold text-white">Past Events</h2>
                    </div>
                )}
                
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎭</div>
                    <p className="text-gray-400 mb-4">No recent shows found</p>
                    <p className="text-sm text-gray-500">Past events will appear here</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`recent-shows-widget ${className}`}>
            {showHeader && (
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <Calendar className="w-6 h-6 text-green-400" />
                        <h2 className="text-xl font-bold text-white">Past Events</h2>
                    </div>
                    <Link 
                        to="/calendar" 
                        className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1 transition-colors"
                    >
                        View All
                        <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            )}
            
            <div className="space-y-4">
                {events.map((event, index) => {
                    return (
                        <div key={event.id} className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50 hover:border-green-500/30 transition-all duration-300 group">
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-white group-hover:text-green-300 transition-colors">
                                            {event.title}
                                        </h3>
                                        <span className={`text-xs px-2 py-1 rounded-full ${getEventTypeColor(event.event_type)}`}>
                                            {event.event_type}
                                        </span>
                                        {Boolean(event.is_featured) && (
                                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                        )}
                                    </div>
                                    
                                    {event.venue_name && (
                                        <p className="text-sm text-gray-400 flex items-center gap-1 mb-2">
                                            <MapPin className="w-3 h-3" />
                                            {event.venue_name}{event.city && `, ${event.city}`}
                                        </p>
                                    )}
                                    
                                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                                        <span>{formatDate(event.start_date)}</span>
                                        <span>{calculateDaysAgo(event.start_date)}</span>
                                    </div>
                                </div>
                                
                                <div className="text-right">
                                    <div className="text-sm text-green-400 font-medium">
                                        {formatDate(event.start_date)}
                                    </div>
                                </div>
                            </div>
                            
                            {event.description && (
                                <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                                    {event.description}
                                </p>
                            )}
                            
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    {event.ticket_price && (
                                        <span className="text-sm text-gray-400">
                                            ${event.ticket_price} tickets
                                        </span>
                                    )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <Link 
                                        to={`/events/${event.slug || event.id}`}
                                        className="text-xs bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-1 rounded-lg transition-all duration-200 flex items-center gap-1"
                                    >
                                        <Play className="w-3 h-3" />
                                        Highlights
                                    </Link>
                                    <Link 
                                        to={`/events/${event.slug || event.id}`}
                                        className="text-xs bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-1 rounded-lg transition-all duration-200"
                                    >
                                        View Details
                                    </Link>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {events.length >= limit && (
                <div className="mt-4 text-center">
                    <Link
                        to="/calendar"
                        className="inline-flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition-colors"
                    >
                        View All Past Shows
                        <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            )}
        </div>
    );
};

export default RecentShows;